import React from 'react';
import { Exam } from '../../types/exam';

interface ExamListProps {
  exams: Exam[];
  onCreateNew: () => void;
  onViewDetail: (exam: Exam) => void;
  onDelete: (examId: string) => void;
}

export const ExamList: React.FC<ExamListProps> = ({
  exams,
  onCreateNew,
  onViewDetail,
  onDelete,
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800';
      case 'SUBMITTED':
        return 'bg-blue-100 text-blue-800';
      case 'PUBLISHED':
        return 'bg-green-100 text-green-800';
      case 'CLOSED':
        return 'bg-yellow-100 text-yellow-800';
      case 'ARCHIVED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (exams.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg mb-6">No exams created yet</p>
        <button
          onClick={onCreateNew}
          className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded font-medium transition"
        >
          Create Your First Exam
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-800">My Exams</h2>
        <button
          onClick={onCreateNew}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded font-medium transition"
        >
          + Create New Exam
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {exams.map((exam) => (
          <div key={exam.examId} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-800 mb-1">{exam.title}</h3>
                <p className="text-sm text-gray-600">{exam.description}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(exam.status)}`}>
                {exam.status}
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-sm">
              <div>
                <p className="text-gray-500">Subject</p>
                <p className="font-medium text-gray-800">{exam.subject}</p>
              </div>
              <div>
                <p className="text-gray-500">Total Marks</p>
                <p className="font-medium text-gray-800">{exam.totalMarks}</p>
              </div>
              <div>
                <p className="text-gray-500">Duration</p>
                <p className="font-medium text-gray-800">{exam.duration} mins</p>
              </div>
              <div>
                <p className="text-gray-500">Attempts</p>
                <p className="font-medium text-gray-800">{exam.totalAttempts}</p>
              </div>
            </div>

            <div className="text-xs text-gray-500 mb-4">
              <p>Active: {formatDate(exam.startDate)} to {formatDate(exam.endDate)}</p>
              <p className="mt-1">
                Type: {(exam.examType || 'GENERAL').replace('_', ' ')}
                {exam.chapters?.length ? ` | Chapters: ${exam.chapters.join(', ')}` : ''}
              </p>
              <p className="mt-1">
                Results: {exam.resultPublicationMode === 'MANUAL'
                  ? exam.resultsPublished
                    ? 'Published'
                    : 'Manual publish pending'
                  : 'Immediate'}
                {' | '}
                Certificates: {exam.certificatesEnabled === false ? 'Off' : 'Auto'}
              </p>
            </div>

            {exam.status === 'PUBLISHED' && exam.totalAttempts > 0 && (
              <div className="bg-blue-50 p-3 rounded mb-4 text-sm">
                <p className="text-blue-900">
                  Average Score: <strong>{exam.averageScore?.toFixed(2) || 0}%</strong> | 
                  Pass Rate: <strong>{exam.passRate?.toFixed(1) || 0}%</strong>
                </p>
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => onViewDetail(exam)}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm font-medium transition"
              >
                View Details
              </button>
              <button
                onClick={() => onDelete(exam.examId)}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded text-sm font-medium transition"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ExamList;
